import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CheckCircle2Icon, ShieldCheckIcon, ClockIcon, UsersIcon, ScaleIcon } from 'lucide-react';
const fadeInUp = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-100px'
  },
  transition: {
    duration: 0.8,
    ease: 'easeOut'
  }
};
const staggerContainer = {
  initial: {
    opacity: 0
  },
  whileInView: {
    opacity: 1
  },
  viewport: {
    once: true,
    margin: '-100px'
  },
  transition: {
    staggerChildren: 0.2
  }
};
export function About() {
  return <div className="w-full pt-24 bg-brand-offwhite" data-id="element-85">
      {/* Page Header */}
      <section className="relative py-20 bg-brand-charcoal text-white overflow-hidden" data-id="element-86">
        <div className="absolute inset-0 z-0 opacity-20" data-id="element-87">
          <img src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&q=80&w=2000" alt="Law firm team meeting" className="w-full h-full object-cover" data-id="element-88" />
        </div>
        <div className="relative z-10 max-w-container mx-auto px-6 text-center" data-id="element-89">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="font-serif text-5xl md:text-6xl font-bold mb-6" data-id="element-90">
            About Us
          </motion.h1>
          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="text-xl text-gray-300 max-w-2xl mx-auto" data-id="element-91">
            A modern law firm built on traditional values of integrity,
            excellence, and client-centered service.
          </motion.p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-24" data-id="element-92">
        <div className="max-w-container mx-auto px-6" data-id="element-93">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-94">
            <motion.div {...fadeInUp} data-id="element-95">
              <h2 className="font-serif text-4xl md:text-5xl font-bold text-brand-green mb-6" data-id="element-96">
                Your Trusted Legal Partner in Botswana
              </h2>
              <div className="w-20 h-1 bg-brand-gold mb-8" data-id="element-97"></div>
              <div className="space-y-6 text-lg text-gray-600 leading-relaxed" data-id="element-98">
                <p data-id="element-99">
                  At McErick Attorneys, we believe that effective legal
                  representation begins with understanding our clients. We take
                  the time to listen, analyze, and develop strategies tailored
                  to your specific needs and objectives.
                </p>
                <p data-id="element-100">
                  Founded on the principles of integrity and excellence, our
                  firm has grown to become a trusted name in Botswana's legal
                  landscape. We serve a diverse clientele ranging from
                  individuals and families to entrepreneurs and large
                  corporations.
                </p>
                <p data-id="element-101">
                  Whether you require assistance with property transfers,
                  divorce proceedings, debt recovery, labour disputes or
                  commercial legal matters, our team is committed to protecting
                  your interests through practical legal solutions and
                  professional service.
                </p>
              </div>
            </motion.div>
            <motion.div {...fadeInUp} className="relative" data-id="element-102">
              <div className="grid grid-cols-2 gap-4" data-id="element-103">
                <img src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&q=80&w=800" alt="Legal consultation" className="rounded-2xl w-full h-64 object-cover shadow-soft mt-8" data-id="element-104" />
                <img src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&q=80&w=800" alt="Corporate office meeting" className="rounded-2xl w-full h-64 object-cover shadow-soft" data-id="element-105" />
              </div>
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white p-6 rounded-xl shadow-card text-center w-48" data-id="element-106">
                <ScaleIcon className="w-12 h-12 text-brand-gold mx-auto mb-2" data-id="element-107" />
                <p className="font-serif font-bold text-brand-charcoal text-xl" data-id="element-108">
                  Excellence in Law
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-white" data-id="element-109">
        <div className="max-w-container mx-auto px-6" data-id="element-110">
          <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto mb-16" data-id="element-111">
            <h2 className="font-serif text-4xl font-bold text-brand-green mb-6" data-id="element-112">
              Why Choose McErick
            </h2>
            <p className="text-lg text-gray-600" data-id="element-113">
              We differentiate ourselves through our commitment to client
              success and our practical approach to complex legal challenges.
            </p>
          </motion.div>

          <motion.div variants={staggerContainer} initial="initial" whileInView="whileInView" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-114">
            {[{
            title: 'Professional Legal Advice',
            icon: ShieldCheckIcon,
            desc: 'Expert guidance grounded in deep legal knowledge and experience.'
          }, {
            title: 'Practical Solutions',
            icon: CheckCircle2Icon,
            desc: 'We focus on outcomes that make business and personal sense.'
          }, {
            title: 'Client-Centred Service',
            icon: UsersIcon,
            desc: 'Your goals and peace of mind are our primary objectives.'
          }, {
            title: 'Integrity & Confidentiality',
            icon: ScaleIcon,
            desc: 'Unwavering ethical standards in all our dealings.'
          }, {
            title: 'Efficient Turnaround',
            icon: ClockIcon,
            desc: 'Timely execution without compromising on quality.'
          }, {
            title: 'Experienced Professionals',
            icon: ShieldCheckIcon,
            desc: 'A dedicated team with a proven track record of success.'
          }].map((feature, idx) => <motion.div key={idx} variants={fadeInUp} className="p-8 rounded-2xl bg-brand-offwhite border border-gray-100 hover:shadow-soft transition-shadow" data-id="element-115">
                <feature.icon className="w-10 h-10 text-brand-gold mb-6" data-id="element-116" />
                <h3 className="font-serif text-xl font-bold text-brand-charcoal mb-3" data-id="element-117">
                  {feature.title}
                </h3>
                <p className="text-gray-600" data-id="element-118">{feature.desc}</p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* Our Process Timeline */}
      <section className="py-24 bg-brand-charcoal text-white" data-id="element-119">
        <div className="max-w-container mx-auto px-6" data-id="element-120">
          <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto mb-20" data-id="element-121">
            <h2 className="font-serif text-4xl font-bold text-brand-gold mb-6" data-id="element-122">
              Our Legal Process
            </h2>
            <p className="text-lg text-gray-300" data-id="element-123">
              A transparent, structured approach to resolving your legal
              matters.
            </p>
          </motion.div>

          <div className="relative" data-id="element-124">
            {/* Connecting Line */}
            <div className="hidden md:block absolute top-1/2 left-0 right-0 h-0.5 bg-gray-700 -translate-y-1/2 z-0" data-id="element-125"></div>

            <motion.div variants={staggerContainer} initial="initial" whileInView="whileInView" className="grid grid-cols-1 md:grid-cols-4 gap-12 relative z-10" data-id="element-126">
              {[{
              step: '01',
              title: 'Consultation',
              desc: 'Meet with one of our attorneys to discuss your legal matter.'
            }, {
              step: '02',
              title: 'Case Assessment',
              desc: 'We review your matter and provide practical legal guidance.'
            }, {
              step: '03',
              title: 'Legal Strategy',
              desc: 'Preparation of legal documentation and tailored legal solutions.'
            }, {
              step: '04',
              title: 'Representation',
              desc: 'Professional representation until your matter is concluded.'
            }].map((item, idx) => <motion.div key={idx} variants={fadeInUp} className="relative" data-id="element-127">
                  <div className="w-16 h-16 rounded-full bg-brand-green border-4 border-brand-charcoal flex items-center justify-center text-xl font-bold text-brand-gold mx-auto mb-6 relative z-10" data-id="element-128">
                    {item.step}
                  </div>
                  <div className="text-center" data-id="element-129">
                    <h3 className="font-serif text-xl font-bold mb-3" data-id="element-130">
                      {item.title}
                    </h3>
                    <p className="text-gray-400 text-sm" data-id="element-131">{item.desc}</p>
                  </div>
                </motion.div>)}
            </motion.div>
          </div>
        </div>
      </section>
    </div>;
}